/* eslint-disable react/jsx-no-bind */
/* eslint-disable max-len */
import PropTypes from 'prop-types';

import Field from 'Component/Field';
import Html from 'Component/Html';
import { MixType } from 'Type/Common.type';
import { AttributeType } from 'Type/ProductList.type';
import { noopFn } from 'Util/Common';
import { getBooleanLabel } from 'Util/Product';
import FIELD_TYPE from 'Util/Validator/Field.config';

import { STRING_ONLY_ATTRIBUTE_CODES, SWATCH_LARGE, SWATCH_SMALL } from './ProductOption.config';

/** @namespace Bodypwa/Component/ProductOption/Component/ProductOption */
export const ProductOption = ({
    onClick = noopFn,
    attribute,
    isSelected = false,
    isAvailable = true,
    mix = {},
    isFormattedAsText = false,
    isProductCountVisible = false,
    isPlp = false,
    isFilter = false,
    swatchClass = '',
    sizeSwatch = SWATCH_SMALL
}) => {
    const isNotAvailable = isPlp ? !isPlp : !isAvailable;
    const disbaledClass = isNotAvailable ? 'opacity-40 pointer-events-none' : '';
    const getOptionLabel = (value) => {
        const { attribute_options, is_boolean } = attribute;

        if (attribute_options) {
            const optionValues = attribute_options[value];

            if (optionValues) {
                if (!isProductCountVisible) {
                    const { label } = optionValues;
                    return { ...optionValues, labelText: label };
                }

                const { label, count = 0 } = optionValues;
                const adjustedLabel = getBooleanLabel(label, is_boolean);

                return {
                    ...optionValues,
                    label: `${adjustedLabel} (${count})`,
                    labelText: adjustedLabel,
                    count
                };
            }
        }

        return {};
    };

    const sizeClassMap = {
        [SWATCH_SMALL]: {
            size: 'h-6 w-6 ring-0',
            hover: 'lg:group-hover:ring-1 lg:group-hover:ring-neutral-700 lg:group-hover:ring-offset-1',
            selected: '!ring-1 ring-neutral-700 ring-offset-1'
        },
         [SWATCH_LARGE]: {
            size: 'h-9 w-9 ring-0',
            hover: 'lg:group-hover:ring-2 lg:group-hover:ring-neutral-700 lg:group-hover:border-2 lg:group-hover:border-solid lg:group-hover:border-bmBg-default',
            selected: '!ring-2 ring-neutral-700 border-2 border-solid border-bmBg-default'
         }
    };

    const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(attribute);
    };

    const getCheckboxLabel = (label, subLabel) => (
        <span>
            { label }
            <strong
                className="font-normal text-p14 text-bmText-muted"
            >
                { ` (${subLabel})` }
            </strong>
        </span>
    );

    const renderDropdown = (value, subLabel) => (
        <Field
            type={ FIELD_TYPE.checkbox }
            attr={ {
                id: value,
                name: value,
                checked: isSelected
            } }
            label={ getCheckboxLabel(value, subLabel) }
        />
    );

    const renderStringValue = (value, label, count) => {
        const isSwatch = label;

        const productOptionHover = `
            md:hover:border-bmBorder-strong 
            md:active:border-bmBorder-strong 
            md:hover:shadow-[inset_0_0_0_1px_var(--color-neutral-1000)]
            `;

        const isSelectedClass = isSelected ? 'border-bmBorder-strong border-solid border shadow-[inset_0_0_0_1px_var(--color-neutral-1000)] text-bmText-default'
            : 'border-bmBorder-default border-solid border text-bmText-default';

        if (isFormattedAsText) {
            return label || value || __('N/A');
        }

        if (!isSwatch || isFilter) {
            return renderDropdown(value, count);
        }

        return (
            <span
                className={ `w-fit whitespace-nowrap cursor-pointer block relative text-p14 py-3 px-4 font-normal text-center duration-200 ease-in-out
                rounded-none h-11 ${isSelectedClass} ${productOptionHover}` }
                title={ label }
            >
                { value }
                { isNotAvailable && (
                    <div className="pointer-events-none absolute left-1/2 top-1/2 w-[42px] h-px
                        bg-bmText-default transform -translate-x-1/2 -translate-y-1/2 -rotate-45"
                    />
                ) }
            </span>
        );
    };

    // TODO: Implement `subLabel` and apply styles: font-normal text-p14 text-bmText-muted
    const renderImageValue = (img, label) => {
        if (isFormattedAsText) {
            return label || __('N/A');
        }

        return (
            <div className="group p-0.5 relative">
                { !isAvailable && (
                    <span className="absolute top-1/2 left-1/2 w-px h-[46px]
                        bg-bmText-default transform -translate-x-1/2 -translate-y-1/2 rotate-45 z-20"
                    />
                ) }
                <img
                    className={ `inline-block cursor-pointer rounded-full transition-all duration-200 ease-in-out ${sizeClassMap[sizeSwatch].size}
                        ${isSelected && sizeClassMap[sizeSwatch].selected} ${sizeClassMap[sizeSwatch].hover} ${swatchClass}` }
                    src={ `/media/attribute/swatch/${img}` }
                    alt={ label }
                    loading="lazy"
                />
                <data
                    value={ label }
                    title={ label }
                />
                { (isFilter && <span>{ label }</span>) }
            </div>
        );
    };

    const renderSelectAttribute = () => {
        const { attribute_value, attribute_code, has_swatch } = attribute;
        const attributeOption = getOptionLabel(attribute_value);
        const {
            label, labelText, count, swatch_data
        } = attributeOption;

        if (!swatch_data && has_swatch) {
            return null;
        }

        if (!swatch_data || STRING_ONLY_ATTRIBUTE_CODES.includes(attribute_code)) {
            return renderStringValue(labelText || __('N/A'), null, count);
        }

        const { value, type } = swatch_data;

        switch (type) {
            case '0':
                return renderStringValue(value, labelText, count);
            case '2':
                return renderImageValue(value, label);
            default:
                return renderStringValue(labelText || __('N/A'), labelText, count);
        }
    };

    const renderAttributeByType = () => {
        const { attribute_type } = attribute;

        switch (attribute_type) {
            case 'select':
                return renderSelectAttribute();
            case 'boolean':
                return renderStringValue(+attribute.attribute_value ? __('Yes') : __('No'));
            case 'text':
                return renderStringValue(attribute.attribute_value);
            case 'multiselect':
                return renderStringValue(
                    attribute.attribute_value.split(',')
                        .map((value) => getOptionLabel(value).label).join(', ') || __('N/A')
                );
            case 'media_image':
                return attribute.attribute_value && attribute.attribute_value !== 'no_selection' ? (
                    <img
                        src={ `/media/catalog/product${attribute.attribute_value}` }
                        alt={ attribute.attribute_label }
                        loading="lazy"
                    />
                ) : (
                    <div className="" />
                );
            case 'textarea':
                return (
                    <div>
                        <Html content={ attribute.attribute_value } />
                    </div>
                );
            case 'weight':
                return renderStringValue(parseFloat(attribute.attribute_value).toFixed(2));
            default:
                return <div className="" />;
        }
    };

    if (attribute.attribute_code && !attribute.attribute_value) {
        return null;
    }

    if (isFormattedAsText) {
        return (
            <span>
                { renderAttributeByType() }
            </span>
        );
    }

    return (
        <span
            onClick={ clickHandler }
            onKeyDown={ clickHandler }
            role="link"
            tabIndex="-1"
            className={ disbaledClass }
            aria-hidden={ isNotAvailable }
            mix={ mix }
        >
            { renderAttributeByType() }
        </span>
    );
};

ProductOption.propTypes = {
    onClick: PropTypes.func,
    attribute: AttributeType.isRequired,
    isSelected: PropTypes.bool,
    isAvailable: PropTypes.bool,
    mix: MixType,
    isFormattedAsText: PropTypes.bool,
    isProductCountVisible: PropTypes.bool,
    isPlp: PropTypes.bool,
    isFilter: PropTypes.bool,
    swatchClass: PropTypes.string,
    sizeSwatch: PropTypes.oneOf([SWATCH_LARGE, SWATCH_SMALL])
};

export default ProductOption;
